import { type ReactNode, useMemo } from 'react';
import { ApiProvider } from '@backstage/core-app-api';
import { useApiHolder } from '@backstage/core-plugin-api';
import {
  type TechRadarApi,
  techRadarApiRef,
} from '@backstage-community/plugin-tech-radar';

import { apiEntry, ScopedApiHolder } from './scoped-api-holder';

export interface TechRadarApiScopeProps {
  /**
   * Per-entity {@link TechRadarApi} implementation. Overlaid on the
   * parent api holder for the duration of `children`.
   */
  catalogTechRadarApi: TechRadarApi;
  children: ReactNode;
}

/**
 * Overlay a per-entity {@link TechRadarApi} on top of the surrounding
 * Backstage api holder. Children resolve `techRadarApiRef` to the
 * supplied instance; every other ref falls through to the parent.
 *
 * Why this wrapper exists:
 *
 *  - The community `TechRadarComponent` resolves its data source via the
 *    global api holder (`techRadarApiRef`) and has no `api` prop, so we
 *    cannot inject the per-entity catalog data through the component
 *    interface directly. (Verified against
 *    `@backstage-community/plugin-tech-radar` `TechRadarComponentProps`,
 *    which exposes only `id`, `width`, `height`, `svgProps`, `searchText`.)
 *
 *  - Registering the api via `createApiFactory` is not viable: factories
 *    install one implementation globally for the lifetime of the app,
 *    but the radar data is per-entity (each TechRadar catalog entity
 *    carries its own `spec.data`). We need a scope that lives and dies
 *    with the entity render.
 *
 *  - `ApiProvider` as a local overlay is the legitimate escape hatch
 *    here: `ScopedApiHolder` only intercepts refs it was told about and
 *    delegates everything else to the parent holder, so the rest of the
 *    Backstage api graph remains intact.
 *
 * Keep this wrapper one-off. The TechRadar community plugin is the only
 * place in this codebase that combines (a) an ApiRef-based data
 * contract with (b) per-entity payloads that vary per route. For
 * everything else (Tools, normal entity content) the standard
 * `useEntity()` / api-factory flow remains the right choice.
 */
export function TechRadarApiScope({
  catalogTechRadarApi,
  children,
}: TechRadarApiScopeProps) {
  const parentHolder = useApiHolder();
  const scopedHolder = useMemo(
    () =>
      new ScopedApiHolder(parentHolder, [
        apiEntry(techRadarApiRef, catalogTechRadarApi),
      ]),
    [parentHolder, catalogTechRadarApi],
  );

  return <ApiProvider apis={scopedHolder}>{children}</ApiProvider>;
}
